/** Local waterfall draft helpers and dry-run plan builders. */

import {ADMOB_NETWORK_SOURCE_ID} from './adFormats.ts';
import {
    ADMOB_NETWORK_WATERFALL_SOURCE_ID,
    isAdmobWaterfallFloorSource,
    opmcIdFromMapping,
} from './opmcWaterfall.ts';
import {getWaterfallSource} from './waterfallSources/registry.ts';

export type WaterfallRow = {
    /** Local stable key for UI (not always upstream line id). */
    key: string;
    lineId: string;
    displayName: string;
    adUnitId: string;
    floorPrice: number | null;
    /** Existing selection from ad-units dropdown. */
    selectedAdUnitId: string;
    /** true when row existed upstream before edit. */
    isExisting: boolean;
    /** Marked for deletion in draft. */
    deleted: boolean;
    /** Fields locked after selecting an existing ad unit. */
    lockedExceptFloor: boolean;
    /** Mapping currently points at a targeting (standard) ad unit. */
    mapsToStandard: boolean;
    /** Which waterfall (ad source) this line belongs to. */
    waterfallId: string;
    adSourceId: string;
    cpmMode: string;
    /** create = new OPMC chain; existing = pick OPMC id then mapping+line */
    opmcBindMode: 'create' | 'existing';
    /** Full admobNetworkWaterfallAdUnitId when opmcBindMode === 'existing' */
    selectedOpmcId: string;
    /**
     * OPMC id already bound to an existing MANUAL line (from mapping config
     * or displayName match). Used to hide that OPMC from pick dropdowns.
     */
    boundOpmcId: string;
    /** Third-party account id (e.g. InMobi). Empty when source has no account API. */
    thirdPartyAccountId: string;
    /** Partner app id for InMobi / Pangle / Vungle (row-level extra config). */
    partnerAppId: string;
    /** AdMob mediation A/B experiment variant on this line (output-only from upstream). */
    experimentVariant: string;
};

export type WaterfallOption = {
    id: string;
    name: string;
    adSourceId: string;
    lineCount: number;
    modeHint?: string;
};

export type DryRunStep = {
    id: string;
    label: string;
    method: 'POST' | 'PATCH';
    path: string;
    change: Record<string, any>;
    notes: string[];
    kind?:
        | 'create-opmc'
        | 'create-remote'
        | 'create-mapping'
        | 'add-waterfall-line'
        | 'patch-line'
        | 'remove-line'
        | 'patch-targeting';
    dependsOn?: string;
    /** Standard ad unit external id for mappings map key */
    standardAdUnitId?: string;
    /** Resource id segment for mapping POST path; filled when known at plan time */
    standardAdUnitResourceId?: string;
    rowKey?: string;
    /** Extra dry-run body fields (e.g. InMobi account_id) */
    dryRunExtras?: Record<string, any>;
};

export type MissingApiNote = {
    id: string;
    message: string;
};

export type ConfirmPlan = {
    steps: DryRunStep[];
    missingApis: MissingApiNote[];
    warnings: string[];
};

const MICROS_PER_DOLLAR = 1_000_000;

export function dollarsToCpmMicros(dollars: number | null | undefined): string | null {
    if (dollars == null || Number.isNaN(Number(dollars))) {
        return null;
    }
    const micros = Math.round(Number(dollars) * MICROS_PER_DOLLAR);
    if (micros < 10000) {
        // Official minimum is 10000 micros ($0.01)
        return '10000';
    }
    return String(micros);
}

export function cpmMicrosToDollars(micros: string | number | null | undefined): number | null {
    if (micros == null || micros === '') {
        return null;
    }
    const n = Number(micros);
    if (Number.isNaN(n)) {
        return null;
    }
    return n / MICROS_PER_DOLLAR;
}

export function lineAdUnitId(line: any): string {
    const mappings = line?.adUnitMappings || line?.ad_unit_mappings || {};
    if (mappings && typeof mappings === 'object') {
        const keys = Object.keys(mappings);
        if (keys.length) {
            return keys[0];
        }
    }
    return String(line?.adUnitId || line?.ad_unit_id || '');
}

/** Mapping resource name stored as the value in line.adUnitMappings. */
export function lineMappingName(line: any): string {
    const mappings = line?.adUnitMappings || line?.ad_unit_mappings || {};
    if (mappings && typeof mappings === 'object') {
        const keys = Object.keys(mappings);
        if (keys.length) {
            return String(mappings[keys[0]] || '').trim();
        }
    }
    return '';
}

export function linesToDraftRows(
    lines: any[],
    targetingAdUnitIds: string[] = [],
): WaterfallRow[] {
    const targeting = new Set((targetingAdUnitIds || []).map(String));
    return (lines || []).map((line, index) => {
        const lineId = String(line.id || line.lineId || line.key || `line-${index}`);
        const micros = line.cpmMicros ?? line.cpm_micros;
        const adUnitId = lineAdUnitId(line);
        const mapsToStandard = Boolean(adUnitId && targeting.has(adUnitId));
        const adSourceId = String(line.adSourceId || line.ad_source_id || '');
        const cpmMode = String(line.cpmMode || line.cpm_mode || '').toUpperCase();
        return {
            key: `existing-${lineId}`,
            lineId,
            displayName: String(line.displayName || line.display_name || ''),
            adUnitId,
            floorPrice: cpmMicrosToDollars(micros),
            selectedAdUnitId: mapsToStandard ? '' : adUnitId,
            isExisting: true,
            deleted: false,
            lockedExceptFloor: Boolean(adUnitId) && !mapsToStandard,
            mapsToStandard,
            waterfallId: adSourceId,
            adSourceId,
            cpmMode,
            opmcBindMode: 'create',
            selectedOpmcId: '',
            boundOpmcId: '',
            thirdPartyAccountId: '',
            partnerAppId: '',
            experimentVariant: String(line.experimentVariant || line.experiment_variant || '').toUpperCase(),
        };
    });
}

export function newDraftRow(index: number, waterfallId = '', cpmMode = 'MANUAL'): WaterfallRow {
    const mode = String(cpmMode || 'MANUAL').toUpperCase() === 'LIVE' ? 'LIVE' : 'MANUAL';
    return {
        key: `new-${Date.now()}-${index}`,
        lineId: '',
        displayName: '',
        adUnitId: '',
        floorPrice: null,
        selectedAdUnitId: '',
        isExisting: false,
        deleted: false,
        lockedExceptFloor: false,
        mapsToStandard: false,
        waterfallId,
        adSourceId: waterfallId,
        cpmMode: mode,
        opmcBindMode: 'create',
        selectedOpmcId: '',
        boundOpmcId: '',
        thirdPartyAccountId: '',
        partnerAppId: '',
        experimentVariant: '',
    };
}

export function rowBelongsToAdSource(row: WaterfallRow, adSourceId: string): boolean {
    const id = String(adSourceId || '');
    return String(row.adSourceId || row.waterfallId || '') === id;
}

/** View filter for mixed waterfall editor: keep rows whose platform is selected, sort by floor desc. */
export function filterAndSortMixedRows(
    rows: WaterfallRow[],
    selectedPlatformIds: string[],
): WaterfallRow[] {
    const selected = new Set((selectedPlatformIds || []).map(String).filter(Boolean));
    if (!selected.size) {
        return [];
    }
    const filtered = (rows || []).filter((row) =>
        selected.has(String(row.adSourceId || row.waterfallId || '')),
    );
    return filtered.slice().sort((a, b) => {
        // Soft-deleted rows always sink to the bottom
        if (Boolean(a.deleted) !== Boolean(b.deleted)) {
            return a.deleted ? 1 : -1;
        }
        const fa = a.floorPrice;
        const fb = b.floorPrice;
        const aBad = fa == null || Number.isNaN(Number(fa));
        const bBad = fb == null || Number.isNaN(Number(fb));
        if (aBad && bBad) {
            return String(a.key).localeCompare(String(b.key));
        }
        if (aBad) {
            return 1;
        }
        if (bBad) {
            return -1;
        }
        const diff = Number(fb) - Number(fa);
        if (diff !== 0) {
            return diff;
        }
        return String(a.key).localeCompare(String(b.key));
    });
}

/** Resolve a display name that does not collide with `usedNames`. */
export function uniqueImportDisplayName(baseName: string, usedNames: Set<string>): string {
    const base = String(baseName || '').trim() || 'layer';
    if (!usedNames.has(base)) {
        return base;
    }
    const first = `${base}-import`;
    if (!usedNames.has(first)) {
        return first;
    }
    let n = 2;
    while (usedNames.has(`${base}-import-${n}`)) {
        n += 1;
    }
    return `${base}-import-${n}`;
}

export type RowsFromImportedWaterfallInput = {
    lines: any[];
    startIndex: number;
    usedNamesBySource: Map<string, Set<string>>;
    isSupportedSource: (id: string) => boolean;
};

/**
 * Map another mediation group's waterfall lines into create-only draft rows.
 * Does not bind existing partner units.
 */
export function rowsFromImportedWaterfall(
    input: RowsFromImportedWaterfallInput,
): {rows: WaterfallRow[]; skipped: number} {
    const rows: WaterfallRow[] = [];
    let skipped = 0;
    const lines = input.lines || [];
    let index = Number(input.startIndex) || 0;
    for (const line of lines) {
        const adSourceId = String(
            line?.adSourceId || line?.ad_source_id || line?.waterfallId || '',
        ).trim();
        if (!adSourceId || !input.isSupportedSource(adSourceId)) {
            skipped += 1;
            continue;
        }
        let used = input.usedNamesBySource.get(adSourceId);
        if (!used) {
            used = new Set();
            input.usedNamesBySource.set(adSourceId, used);
        }
        const rawName = String(line?.displayName || line?.display_name || '').trim();
        const displayName = uniqueImportDisplayName(rawName || 'layer', used);
        used.add(displayName);
        const micros = line?.cpmMicros ?? line?.cpm_micros;
        const cpmModeRaw = String(line?.cpmMode || line?.cpm_mode || 'MANUAL').toUpperCase();
        const cpmMode = cpmModeRaw === 'LIVE' ? 'LIVE' : 'MANUAL';
        const row = newDraftRow(index, adSourceId, cpmMode);
        row.displayName = displayName;
        row.floorPrice = cpmMicrosToDollars(micros);
        row.isExisting = false;
        row.opmcBindMode = 'create';
        row.selectedOpmcId = '';
        row.boundOpmcId = '';
        row.lineId = '';
        row.adUnitId = '';
        row.selectedAdUnitId = '';
        row.lockedExceptFloor = false;
        row.mapsToStandard = false;
        rows.push(row);
        index += 1;
    }
    return {rows, skipped};
}

export function inferOpmcBindMode(
    selectedOpmcId: string | null | undefined,
): 'create' | 'existing' {
    const trimmed = String(selectedOpmcId ?? '').trim();
    return trimmed ? 'existing' : 'create';
}

function rowOccupiedOpmcId(row: WaterfallRow): string {
    if (row.deleted) {
        return '';
    }
    const selected = String(row.selectedOpmcId || '').trim();
    if (selected) {
        return selected;
    }
    const bound = String(row.boundOpmcId || '').trim();
    if (bound) {
        return bound;
    }
    const isExistingLine = Boolean(row.isExisting || row.lineId);
    const isManual = String(row.cpmMode || '').toUpperCase() !== 'LIVE';
    if (isExistingLine && isManual && !row.mapsToStandard) {
        return String(row.adUnitId || '').trim();
    }
    return '';
}

/** Build mapping.name → OPMC id from GET .../mappings payload items. */
export function indexOpmcIdByMappingName(mappings: any[]): Map<string, string> {
    const out = new Map<string, string>();
    for (const mapping of mappings || []) {
        const name = String(mapping?.name || '').trim();
        const opmcId = opmcIdFromMapping(mapping);
        if (name && opmcId) {
            out.set(name, opmcId);
        }
    }
    return out;
}

/** Attach boundOpmcId on draft rows using each upstream line's mapping name. */
export function withBoundOpmcIds(
    rows: WaterfallRow[],
    lines: any[],
    opmcByMappingName: Map<string, string>,
): WaterfallRow[] {
    return (rows || []).map((row, index) => {
        if (String(row.boundOpmcId || '').trim()) {
            return row;
        }
        const mappingName = lineMappingName(lines?.[index]);
        const opmcId = mappingName ? String(opmcByMappingName.get(mappingName) || '').trim() : '';
        if (!opmcId) {
            return row;
        }
        return {...row, boundOpmcId: opmcId};
    });
}

/**
 * Fallback when mappings are unavailable: existing MANUAL lines whose
 * displayName matches an OPMC unit occupy that OPMC id.
 */
export function fillBoundOpmcIdsByDisplayName(
    rows: WaterfallRow[],
    opmcUnits: Array<{id: string; displayName?: string}>,
): WaterfallRow[] {
    const byName = new Map<string, string>();
    for (const unit of opmcUnits || []) {
        const name = String(unit.displayName || '').trim();
        const id = String(unit.id || '').trim();
        if (name && id && !byName.has(name)) {
            byName.set(name, id);
        }
    }
    return (rows || []).map((row) => {
        if (row.deleted) {
            return row;
        }
        if (String(row.boundOpmcId || '').trim() || String(row.selectedOpmcId || '').trim()) {
            return row;
        }
        const isExistingLine = Boolean(row.isExisting || row.lineId);
        const isManual = String(row.cpmMode || '').toUpperCase() !== 'LIVE';
        if (!isExistingLine || !isManual) {
            return row;
        }
        const name = String(row.displayName || '').trim();
        const opmcId = name ? byName.get(name) || '' : '';
        if (!opmcId) {
            return row;
        }
        return {...row, boundOpmcId: opmcId};
    });
}

/** OPMC ids used by non-deleted rows; optionally limited to one ad source. */
export function occupiedOpmcIds(
    rows: WaterfallRow[],
    exceptRowKey?: string,
    adSourceId?: string,
): Set<string> {
    const out = new Set<string>();
    const except = String(exceptRowKey || '');
    const sourceFilter = String(adSourceId || '');
    for (const row of rows || []) {
        if (except && row.key === except) {
            continue;
        }
        if (sourceFilter && !rowBelongsToAdSource(row, sourceFilter)) {
            continue;
        }
        const id = rowOccupiedOpmcId(row);
        if (id) {
            out.add(id);
        }
    }
    return out;
}

export function filterOpmcUnitsForRow<T>(
    candidates: T[],
    rows: WaterfallRow[],
    row: WaterfallRow,
    unitId: (unit: T) => string,
    unitDisplayName?: (unit: T) => string,
): T[] {
    const sourceId = String(row?.adSourceId || row?.waterfallId || '');
    const occupiedIds = occupiedOpmcIds(rows, row?.key, sourceId || undefined);
    const occupiedNames = new Set<string>();
    const except = String(row?.key || '');
    for (const other of rows || []) {
        if (other.deleted) {
            continue;
        }
        if (except && other.key === except) {
            continue;
        }
        if (sourceId && !rowBelongsToAdSource(other, sourceId)) {
            continue;
        }
        const name = String(other.displayName || '').trim();
        if (name) {
            occupiedNames.add(name);
        }
    }
    const keepSelf = String(row?.selectedOpmcId || '').trim();
    return (candidates || []).filter((unit) => {
        const id = String(unitId(unit) || '').trim();
        if (!id) {
            return false;
        }
        if (keepSelf && id === keepSelf) {
            return true;
        }
        if (occupiedIds.has(id)) {
            return false;
        }
        if (unitDisplayName) {
            const name = String(unitDisplayName(unit) || '').trim();
            if (name && occupiedNames.has(name)) {
                return false;
            }
        }
        return true;
    });
}

/** Row keys whose trimmed displayName collides with another non-deleted row in the same ad source. */
export function duplicateDisplayNameKeys(rows: WaterfallRow[]): Set<string> {
    const byName = new Map<string, string[]>();
    for (const row of rows || []) {
        if (row.deleted) {
            continue;
        }
        const name = String(row.displayName || '').trim();
        if (!name) {
            continue;
        }
        const source = String(row.adSourceId || row.waterfallId || '');
        const groupKey = `${source}\0${name}`;
        const list = byName.get(groupKey) || [];
        list.push(row.key);
        byName.set(groupKey, list);
    }
    const dup = new Set<string>();
    for (const keys of byName.values()) {
        if (keys.length < 2) {
            continue;
        }
        for (const k of keys) {
            dup.add(k);
        }
    }
    return dup;
}

export function hasDuplicateDisplayNames(rows: WaterfallRow[]): boolean {
    return duplicateDisplayNameKeys(rows).size > 0;
}

/** Row keys whose floorPrice micros collide with another non-deleted row in the same ad source. */
export function duplicateFloorPriceKeys(rows: WaterfallRow[]): Set<string> {
    const byMicros = new Map<string, string[]>();
    for (const row of rows || []) {
        if (row.deleted) {
            continue;
        }
        const micros = dollarsToCpmMicros(row.floorPrice);
        if (micros == null) {
            continue;
        }
        const source = String(row.adSourceId || row.waterfallId || '');
        const groupKey = `${source}\0${micros}`;
        const list = byMicros.get(groupKey) || [];
        list.push(row.key);
        byMicros.set(groupKey, list);
    }
    const dup = new Set<string>();
    for (const keys of byMicros.values()) {
        if (keys.length < 2) {
            continue;
        }
        for (const k of keys) {
            dup.add(k);
        }
    }
    return dup;
}

export function hasDuplicateFloorPrices(rows: WaterfallRow[]): boolean {
    return duplicateFloorPriceKeys(rows).size > 0;
}

/**
 * AdMob requires mediation group line labels unique across the whole group
 * (including other ad sources). UI allows same displayName per source; when
 * submitting the line, append a source hint if the label is already taken.
 */
export function mediationLineDisplayNameForSubmit(
    row: WaterfallRow,
    allRows: WaterfallRow[],
    sourceHint?: string,
): string {
    const name = String(row.displayName || '').trim() || 'waterfall_line';
    const myKey = String(row.key || '');
    let conflict = false;
    for (const other of allRows || []) {
        if (other.deleted) {
            continue;
        }
        if (myKey && other.key === myKey) {
            continue;
        }
        if (String(other.displayName || '').trim() === name) {
            conflict = true;
            break;
        }
    }
    if (!conflict) {
        return name;
    }
    const hint =
        String(sourceHint || '').trim() ||
        String(row.adSourceId || row.waterfallId || '').trim().slice(-8) ||
        'src';
    const base = `${name} · ${hint}`;
    const taken = new Set<string>();
    for (const other of allRows || []) {
        if (other.deleted) {
            continue;
        }
        if (myKey && other.key === myKey) {
            continue;
        }
        const otherName = String(other.displayName || '').trim();
        if (otherName) {
            taken.add(otherName);
        }
    }
    if (!taken.has(base)) {
        return base;
    }
    let n = 2;
    while (taken.has(`${base} · ${n}`)) {
        n += 1;
    }
    return `${base} · ${n}`;
}

/** True when trimmed name equals any trimmed entry in existingNames. Empty → false. */
export function displayNameExists(name: string, existingNames: Iterable<string>): boolean {
    const needle = String(name || '').trim();
    if (!needle) {
        return false;
    }
    for (const raw of existingNames || []) {
        if (String(raw || '').trim() === needle) {
            return true;
        }
    }
    return false;
}

/** Whether a mediation group belongs to an app via overlapping ad unit ids. */
export function mediationGroupMatchesApp(
    group: any,
    appAdUnitIds: Set<string> | Iterable<string>,
): boolean {
    const targeting = group?.targeting || {};
    const adUnitIds = targeting.adUnitIds || targeting.ad_unit_ids || [];
    if (!adUnitIds.length) {
        return false;
    }
    const set =
        appAdUnitIds instanceof Set
            ? appAdUnitIds
            : new Set(Array.from(appAdUnitIds || []).map((id) => String(id)));
    if (!set.size) {
        return true;
    }
    return adUnitIds.some((id: any) => set.has(String(id)));
}

export function hasUncommittedDraftForAdSource(
    draftRows: WaterfallRow[],
    originalRows: WaterfallRow[],
    adSourceId: string,
): boolean {
    const origByKey = new Map(originalRows.map((r) => [r.key, r]));
    for (const row of draftRows) {
        if (!rowBelongsToAdSource(row, adSourceId)) {
            continue;
        }
        if (!row.isExisting && !row.lineId) {
            return true;
        }
        if (row.deleted) {
            return true;
        }
        const orig = origByKey.get(row.key) || originalRows.find((o) => o.lineId && o.lineId === row.lineId);
        if (!orig) {
            return true;
        }
        if (Number(orig.floorPrice) !== Number(row.floorPrice)) {
            return true;
        }
        if (String(orig.displayName || '') !== String(row.displayName || '')) {
            return true;
        }
        if (String(orig.opmcBindMode || 'create') !== String(row.opmcBindMode || 'create')) {
            return true;
        }
        if (String(orig.selectedOpmcId || '') !== String(row.selectedOpmcId || '')) {
            return true;
        }
    }
    return false;
}

export function discardUncommittedDraftForAdSource(
    draftRows: WaterfallRow[],
    originalRows: WaterfallRow[],
    adSourceId: string,
): WaterfallRow[] {
    const origByKey = new Map(originalRows.map((r) => [r.key, r]));
    const out: WaterfallRow[] = [];
    for (const row of draftRows) {
        if (!rowBelongsToAdSource(row, adSourceId)) {
            out.push(row);
            continue;
        }
        if (!row.isExisting && !row.lineId) {
            continue; // drop new
        }
        const orig =
            origByKey.get(row.key) ||
            originalRows.find((o) => o.lineId && o.lineId === row.lineId);
        if (orig) {
            out.push({...orig, deleted: false});
        }
    }
    return out;
}

export type AdSourceInput = { id: string; name: string };

/**
 * Build waterfall/ad-source dropdown options from catalog + draft lines.
 * Groups by adSourceId (includes MANUAL waterfall and LIVE bidding).
 */
export function buildWaterfallOptions(
    rows: WaterfallRow[],
    adSourceNameById: Record<string, string> = {},
    adSources?: AdSourceInput[],
): WaterfallOption[] {
    type Entry = { name: string; rows: WaterfallRow[] };
    const bySource = new Map<string, Entry>();

    for (const src of adSources || []) {
        bySource.set(src.id, {name: src.name, rows: []});
    }

    const pool = (rows || []).filter((r) => !r.deleted && r.adSourceId);
    for (const row of pool) {
        const id = row.adSourceId || row.waterfallId;
        if (!bySource.has(id)) {
            const name =
                adSourceNameById[id] ||
                row.displayName ||
                `广告源 ${id.slice(-6)}`;
            bySource.set(id, {name, rows: []});
        } else if (adSourceNameById[id]) {
            bySource.get(id)!.name = adSourceNameById[id];
        }
        bySource.get(id)!.rows.push(row);
    }

    const options = [...bySource.entries()].map(([id, {name, rows: list}]) => {
        const modes = list.map((r) => r.cpmMode).filter(Boolean);
        let modeHint = modes.includes('MANUAL')
            ? 'waterfall'
            : modes.includes('LIVE')
              ? 'bidding'
              : /bidding/i.test(name)
                ? 'bidding'
                : /waterfall/i.test(name)
                  ? 'waterfall'
                  : '';
        if (!modeHint) {
            modeHint = 'waterfall';
        }
        return {
            id,
            adSourceId: id,
            name,
            lineCount: list.length,
            modeHint,
        };
    });

    options.sort((a, b) => {
        const aWf = /waterfall/i.test(a.name) ? 0 : 1;
        const bWf = /waterfall/i.test(b.name) ? 0 : 1;
        if (aWf !== bWf) {
            return aWf - bWf;
        }
        return a.name.localeCompare(b.name, 'zh-CN');
    });

    return options;
}

function activeRows(rows: WaterfallRow[]): WaterfallRow[] {
    return rows.filter((r) => !r.deleted);
}

function orderChanged(original: WaterfallRow[], draft: WaterfallRow[]): boolean {
    const a = activeRows(original).map((r) => r.lineId || r.key);
    const b = activeRows(draft).map((r) => r.lineId || r.key);
    if (a.length !== b.length) {
        return false;
    }
    return a.some((id, i) => id !== b[i]);
}

export type BuildConfirmPlanInput = {
    accountId: string;
    mediationGroupId: string;
    appId: string;
    adFormat: string;
    adTypes: string[];
    adSourceId: string;
    originalRows: WaterfallRow[];
    draftRows: WaterfallRow[];
    /** Targeting ad unit ids on the mediation group (standard units). */
    originalTargetingAdUnitIds: string[];
    draftTargetingAdUnitIds: string[];
    /** Resource id segment for mapping POST; optional until Vue resolves it. */
    standardAdUnitResourceId?: string;
    /** ANDROID | IOS — for waterfall mapping adapter selection */
    platform?: string;
    /** Partner context forwarded into each adapter.buildConfirmSteps */
    thirdPartyAccountId?: string;
    inmobiAppId?: string;
    packageName?: string;
    applovinSdkKey?: string;
    mintegralAppKey?: string;
    mintegralAppId?: string;
    mintegralPlacementId?: string;
};

/**
 * Build sequential dry-run steps for Confirm.
 * Does not call apply. Surfaces missing APIs as notes.
 */
export function buildConfirmPlan(input: BuildConfirmPlanInput): ConfirmPlan {
    const {
        accountId,
        mediationGroupId,
        appId,
        adFormat,
        adTypes,
        originalRows,
        draftRows,
        originalTargetingAdUnitIds,
        draftTargetingAdUnitIds,
        standardAdUnitResourceId,
        platform,
        thirdPartyAccountId,
        inmobiAppId,
        packageName,
        applovinSdkKey,
        mintegralAppKey,
        mintegralAppId,
        mintegralPlacementId,
    } = input;

    const steps: DryRunStep[] = [];
    const missingApis: MissingApiNote[] = [];
    const warnings: string[] = [];

    const patchPath = `/admob/accounts/${accountId}/mediation-groups/${mediationGroupId}`;
    const standardAdUnitId = String(draftTargetingAdUnitIds?.[0] || '').trim();
    const mappingResourceId = String(standardAdUnitResourceId || '').trim();

    const originalByKey = new Map(originalRows.map((r) => [r.key, r]));
    let tempLineCounter = -1;

    if (orderChanged(originalRows, draftRows)) {
        warnings.push('拖拽顺序仅保存在本地；AdMob 瀑布流通常按 CPM 排序，确认不会提交顺序字段。');
    }

    const targetingChanged =
        JSON.stringify([...originalTargetingAdUnitIds].sort()) !==
        JSON.stringify([...draftTargetingAdUnitIds].sort());
    if (targetingChanged) {
        steps.push({
            id: 'patch-targeting',
            kind: 'patch-targeting',
            label: '修改中介组绑定广告单元 (targeting.adUnitIds)',
            method: 'PATCH',
            path: patchPath,
            change: {
                updateMask: 'targeting.ad_unit_ids',
                targeting: {adUnitIds: draftTargetingAdUnitIds},
            },
            notes: [],
        });
    }

    for (const row of draftRows) {
        if (row.deleted) {
            if (!row.isExisting || !row.lineId) {
                continue;
            }
            steps.push({
                id: `remove-line-${row.lineId}`,
                kind: 'remove-line',
                label: `删除瀑布流行 ${row.displayName || row.lineId}`,
                method: 'PATCH',
                path: patchPath,
                change: {
                    updateMask: `mediationGroupLines["${row.lineId}"].state`,
                    mediationGroupLines: {
                        [row.lineId]: {id: row.lineId, state: 'REMOVED'},
                    },
                },
                notes: ['删除的是 mediation group line（state=REMOVED），不会删除广告单元实体。'],
                rowKey: row.key,
            });
            missingApis.push({
                id: `no-delete-ad-unit-${row.lineId}`,
                message: `中间层无 DELETE/PATCH ad unit；行「${row.displayName || row.adUnitId}」对应广告单元不会被删除。`,
            });
            continue;
        }

        if (!row.isExisting) {
            const rowSourceId = String(row.adSourceId || row.waterfallId || '').trim();
            const rowMode = String(row.cpmMode || 'MANUAL').toUpperCase();

            // Bidding LIVE under classic AdMob Network is created with the mediation group.
            if (rowSourceId === ADMOB_NETWORK_SOURCE_ID && rowMode === 'LIVE') {
                warnings.push(
                    `新建行「${row.displayName || row.key}」是 AdMob Network 出价（LIVE）：创建中介组时已自带，无需再新建，也不会创建 OPMC。`,
                );
                missingApis.push({
                    id: `skip-live-admob-network-${row.key}`,
                    message:
                        `出价 AdMob Network（LIVE）已随中介组创建；请删除该新建行。瀑布流分层请在「瀑布流」面板新建 MANUAL 行（走 OPMC）。`,
                });
                continue;
            }

            if (rowMode === 'LIVE' && rowSourceId) {
                missingApis.push({
                    id: `third-party-live-${row.key}`,
                    message: `新建行「${row.displayName || row.key}」是第三方出价 LIVE（${rowSourceId}）；本版本未实现出价新建。`,
                });
                continue;
            }

            if (!rowSourceId) {
                missingApis.push({
                    id: `no-ad-source-${row.key}`,
                    message: `新建行「${row.displayName || row.key}」缺少广告源（adSourceId/waterfallId），无法创建瀑布流行。`,
                });
                continue;
            }

            // Classic AdMob Network MANUAL floors still use the OPMC adapter chain.
            let adapterSourceId = rowSourceId;
            if (isAdmobWaterfallFloorSource(rowSourceId, rowMode)) {
                adapterSourceId = ADMOB_NETWORK_WATERFALL_SOURCE_ID;
            }

            const adapter = getWaterfallSource(adapterSourceId);
            if (!adapter) {
                missingApis.push({
                    id: `third-party-bind-${row.key}`,
                    message: `新建行「${row.displayName || row.key}」广告源 ${rowSourceId} 非 AdMob Network Waterfall；第三方瀑布流绑定本版本未实现。`,
                });
                continue;
            }

            const result = adapter.buildConfirmSteps({
                accountId,
                mediationGroupId,
                appId,
                adFormat,
                adTypes,
                platform,
                patchPath,
                standardAdUnitId,
                standardAdUnitResourceId: mappingResourceId,
                row,
                draftRows,
                lineLabelHint: adapter.displayNameHint,
                allocTempLineId: () => String(tempLineCounter--),
                thirdPartyAccountId:
                    thirdPartyAccountId || row.thirdPartyAccountId || undefined,
                inmobiAppId: inmobiAppId || row.partnerAppId || undefined,
                partnerAppId: row.partnerAppId || undefined,
                packageName,
                applovinSdkKey,
                mintegralAppKey,
                mintegralAppId,
                mintegralPlacementId,
            });
            steps.push(...result.steps);
            missingApis.push(...result.missingApis);
            warnings.push(...result.warnings);
            continue;
        }

        const orig = originalByKey.get(row.key);
        if (!orig) {
            continue;
        }
        const masks: string[] = [];
        const linePatch: Record<string, any> = {id: row.lineId};
        const notes: string[] = [];

        const origMicros = dollarsToCpmMicros(orig.floorPrice);
        const nextMicros = dollarsToCpmMicros(row.floorPrice);
        const isLiveAdmobNetwork =
            String(row.adSourceId || row.waterfallId || '') === ADMOB_NETWORK_SOURCE_ID &&
            String(row.cpmMode || '').toUpperCase() === 'LIVE';
        if (origMicros !== nextMicros && nextMicros != null) {
            if (isLiveAdmobNetwork) {
                const msg =
                    `行「${row.displayName || row.lineId}」是默认 LIVE AdMob Network，底价变更不会提交；请新建 Waterfall 分层行（走 OPMC 链）。`;
                warnings.push(msg);
                missingApis.push({
                    id: `live-admob-network-floor-${row.lineId || row.key}`,
                    message: msg,
                });
            } else {
                masks.push(`mediationGroupLines["${row.lineId}"].cpm_micros`);
                linePatch.cpmMicros = nextMicros;
            }
        }

        if (orig.displayName !== row.displayName && row.displayName) {
            masks.push(`mediationGroupLines["${row.lineId}"].display_name`);
            linePatch.displayName = row.displayName;
            notes.push('若账号权限不支持 display_name mask，上游可能拒绝。');
        }

        if (orig.adUnitId !== row.adUnitId && row.adUnitId) {
            missingApis.push({
                id: `no-remap-${row.lineId}`,
                message: `行 ${row.lineId} 更换 ad unit mapping 需要 mapping 资源名；当前未自动解析，请确认 GET .../mappings 后手工补全（本版 dry-run 不提交 mapping 变更）。`,
            });
        }

        if (masks.length) {
            steps.push({
                id: `patch-line-${row.lineId}`,
                kind: 'patch-line',
                label: `修改瀑布流行 ${row.displayName || row.lineId}`,
                method: 'PATCH',
                path: patchPath,
                change: {
                    updateMask: masks.join(','),
                    mediationGroupLines: {[row.lineId]: linePatch},
                },
                notes,
                rowKey: row.key,
            });
        }

        if (
            (orig.displayName !== row.displayName || orig.adUnitId !== row.adUnitId) &&
            orig.adUnitId
        ) {
            missingApis.push({
                id: `no-patch-ad-unit-${row.lineId}`,
                message: `中间层无 PATCH ad unit；广告单元「${orig.adUnitId}」的名称/实体字段无法更新。`,
            });
        }
    }

    return {steps, missingApis, warnings};
}

export function parseMediationReportByGroup(upstreamData: any): Record<
    string,
    {revenue: number; impressions: number}
> {
    const rows = upstreamData?.rows || [];
    const out: Record<string, {revenue: number; impressions: number}> = {};

    const add = (key: string, revenue: number, impressions: number) => {
        if (!key) {
            return;
        }
        if (!out[key]) {
            out[key] = {revenue: 0, impressions: 0};
        }
        out[key].revenue += revenue;
        out[key].impressions += impressions;
    };

    for (const row of rows) {
        const dims = row?.dimensionValues || row?.dimension_values || {};
        const metrics = row?.metricValues || row?.metric_values || {};
        const groupDim = dims.MEDIATION_GROUP || dims.mediationGroup || {};
        const rawValue = String(groupDim.value || groupDim.Value || '');
        const displayLabel = String(
            groupDim.displayLabel || groupDim.display_label || groupDim.label || '',
        );

        // AdMob mediation report uses values like "ca-app-pub-xxx:mg:{mediationGroupId}"
        let groupId = '';
        const mgMatch = rawValue.match(/:mg:([^:]+)$/);
        if (mgMatch) {
            groupId = mgMatch[1];
        } else if (/^\d+$/.test(rawValue)) {
            groupId = rawValue;
        }

        const earningsMicros = Number(
            metrics.ESTIMATED_EARNINGS?.microsValue ??
                metrics.ESTIMATED_EARNINGS?.micros_value ??
                0,
        );
        const earningsDouble = Number(
            metrics.ESTIMATED_EARNINGS?.doubleValue ??
                metrics.ESTIMATED_EARNINGS?.double_value ??
                0,
        );
        const earnings =
            earningsMicros > 0 ? earningsMicros / MICROS_PER_DOLLAR : earningsDouble;
        const impressions = Number(
            metrics.IMPRESSIONS?.integerValue ??
                metrics.IMPRESSIONS?.integer_value ??
                metrics.IMPRESSIONS?.value ??
                0,
        );

        // Index by id and display name so list join can use either.
        add(groupId, earnings, impressions);
        if (displayLabel && displayLabel !== groupId) {
            add(displayLabel, earnings, impressions);
        }
        if (rawValue && rawValue !== groupId && rawValue !== displayLabel) {
            add(rawValue, earnings, impressions);
        }
    }
    return out;
}

/** Look up metrics for a mediation group using id / name / report raw value. */
export function lookupGroupMetrics(
    metricsByGroup: Record<string, {revenue: number; impressions: number}>,
    group: {mediationGroupId?: string; displayName?: string},
): {revenue: number | null; impressions: number | null} {
    const id = String(group.mediationGroupId || '');
    const name = String(group.displayName || '');
    const hit =
        (id && metricsByGroup[id]) ||
        (name && metricsByGroup[name]) ||
        null;
    if (!hit) {
        return {revenue: null, impressions: null};
    }
    return {revenue: hit.revenue, impressions: hit.impressions};
}

export function recentDateRange(days = 7): {
    startDate: {year: number; month: number; day: number};
    endDate: {year: number; month: number; day: number};
} {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));
    const toParts = (d: Date) => ({
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        day: d.getDate(),
    });
    return {startDate: toParts(start), endDate: toParts(end)};
}

export function filterAdUnitsForWaterfall(
    adUnits: any[],
    adFormat: string,
): any[] {
    return (adUnits || []).filter((unit) => {
        if (!adFormat) {
            return true;
        }
        return String(unit.adFormat || '').toUpperCase() === String(adFormat).toUpperCase();
    });
}

export function unitFormat(unit: any): string {
    return String(unit?.adFormat || '').toUpperCase();
}

/**
 * Ordinary AdUnits for mediation-group targeting (POST/GET .../ad-units).
 * Filter by adFormat (+ optional appId). There is no Google AdUnit "delivery type".
 */
export function filterOrdinaryAdUnits(
    adUnits: any[],
    format: string,
    appId?: string,
): any[] {
    const fmt = String(format || '').toUpperCase();
    if (!fmt) {
        return [];
    }
    return (adUnits || []).filter((unit) => {
        if (unitFormat(unit) !== fmt) {
            return false;
        }
        if (appId && unit.appId && String(unit.appId) !== String(appId)) {
            return false;
        }
        return true;
    });
}

/** Resolve format X from targeting units / group; empty if unknown. */
export function resolveTargetingFormat(
    targetingAdUnitIds: string[],
    adUnits: any[],
    fallbackFormat = '',
): string {
    for (const id of targetingAdUnitIds || []) {
        const unit = (adUnits || []).find((u) => u.adUnitId === id);
        if (unit?.adFormat) {
            return unitFormat(unit);
        }
    }
    return String(fallbackFormat || '').toUpperCase();
}

export function collectFormatMismatchNotes(input: {
    targetingAdUnitIds: string[];
    waterfallAdUnitIds: string[];
    adUnits: any[];
    requiredFormat: string;
}): string[] {
    const notes: string[] = [];
    const required = String(input.requiredFormat || '').toUpperCase();
    if (!required) {
        notes.push('无法确定格式 X（BANNER/INTERSTITIAL/…）；请先选择普通广告单元。');
        return notes;
    }
    const byId = new Map((input.adUnits || []).map((u) => [String(u.adUnitId), u]));
    for (const id of input.targetingAdUnitIds || []) {
        const unit = byId.get(String(id));
        if (unit && unitFormat(unit) !== required) {
            notes.push(
                `普通广告单元 ${unit.displayName || id} 的 format=${unitFormat(unit)}，与要求的 X=${required} 不一致。`,
            );
        }
    }
    for (const id of input.waterfallAdUnitIds || []) {
        const unit = byId.get(String(id));
        if (!unit) {
            continue;
        }
        if (unitFormat(unit) !== required) {
            notes.push(
                `瀑布流行绑定单元 ${unit.displayName || id} 的 format=${unitFormat(unit)} ≠ X=${required}。`,
            );
        }
        // Do NOT warn about targeting overlap / fake delivery type.
    }
    return notes;
}
